/**
 * Base API Service
 * Provides common functionality for all API services
 */
export class BaseApiService {
  protected static readonly BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  protected static readonly LOCALE_STORAGE_KEY = 'gym_planner_locale';

  private static prerenderQueue: Promise<void> = Promise.resolve();
  private static prerenderLastRequestAt = 0;

  private static getPrerenderMinDelayMs(): number {
    const raw = process.env.REACT_APP_PRERENDER_THROTTLE_MS;
    const parsed = raw ? Number(raw) : NaN;
    if (Number.isFinite(parsed) && parsed >= 0) {
      return parsed;
    }
    return 250;
  }

  private static isReactSnapPrerender(): boolean {
    try {
      if (typeof window === 'undefined') {
        return false;
      }
      return Boolean((window as unknown as { __REACT_SNAP__?: boolean }).__REACT_SNAP__);
    } catch {
      return false;
    }
  }

  private static async throttleIfPrerendering(): Promise<void> {
    if (!BaseApiService.isReactSnapPrerender()) {
      return;
    }

    const minDelayMs = BaseApiService.getPrerenderMinDelayMs();
    if (minDelayMs <= 0) {
      return;
    }

    BaseApiService.prerenderQueue = BaseApiService.prerenderQueue.then(async () => {
      const now = Date.now();
      const waitMs = Math.max(0, minDelayMs - (now - BaseApiService.prerenderLastRequestAt));
      if (waitMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, waitMs));
      }
      BaseApiService.prerenderLastRequestAt = Date.now();
    });

    await BaseApiService.prerenderQueue;
  }

  /**
   * Resolve an image URL: if it starts with /api/, prepend the full API base URL.
   * Otherwise return as-is (e.g. external URLs).
   */
  static resolveImageUrl(url?: string): string | undefined {
    if (!url) return undefined;
    if (url.startsWith('/api/')) {
      return `${BaseApiService.BASE_URL}${url.replace('/api', '')}`;
    }
    return url;
  }
  
  // Track endpoints that have permission errors to avoid repeated calls
  private static permissionDeniedEndpoints = new Set<string>();
  
  // Track endpoints that have auth errors to avoid repeated calls after login
  private static unauthorizedEndpoints = new Set<string>();

  private static redirectToLogin(): void {
    try {
      if (typeof window === 'undefined') {
        return;
      }

      localStorage.removeItem('gym_planner_user');
      localStorage.removeItem('gym_planner_token');

      const target = window.location.origin + '/';
      if (window.location.href !== target) {
        window.location.assign(target);
      } else {
        window.location.reload();
      }
    } catch {
      // ignore
    }
  }

  /**
   * Get JWT token from localStorage
   */
  private static getToken(): string | null {
    try {
      return localStorage.getItem('gym_planner_token');
    } catch {
      return null;
    }
  }

  private static getLocale(): string {
    try {
      const locale = localStorage.getItem(BaseApiService.LOCALE_STORAGE_KEY);
      return locale || 'en';
    } catch {
      return 'en';
    }
  }

  /**
   * Check if an endpoint has been marked as permission denied
   */
  private static isPermissionDenied(endpoint: string): boolean {
    return this.permissionDeniedEndpoints.has(endpoint);
  }

  /**
   * Check if an endpoint has been marked as unauthorized
   */
  private static isUnauthorized(endpoint: string): boolean {
    return this.unauthorizedEndpoints.has(endpoint);
  }

  /**
   * Mark an endpoint as permission denied
   */
  private static markPermissionDenied(endpoint: string): void {
    this.permissionDeniedEndpoints.add(endpoint);
    console.log(`🚫 [API] Endpoint marked as permission denied: ${endpoint}`);
  }

  /**
   * Mark an endpoint as unauthorized
   */
  private static markUnauthorized(endpoint: string): void {
    this.unauthorizedEndpoints.add(endpoint);
    console.log(`🔐 [API] Endpoint marked as unauthorized: ${endpoint}`);
  }

  /**
   * Clear permission denied status for an endpoint (for testing or retry)
   */
  public static clearPermissionDenied(endpoint: string): void {
    this.permissionDeniedEndpoints.delete(endpoint);
    console.log(`✅ [API] Cleared permission denied status for: ${endpoint}`);
  }

  /**
   * Clear unauthorized status for an endpoint (for testing or retry)
   */
  public static clearUnauthorized(endpoint: string): void {
    this.unauthorizedEndpoints.delete(endpoint);
    console.log(`✅ [API] Cleared unauthorized status for: ${endpoint}`);
  }

  /**
   * Clear all permission denied status
   */
  public static clearAllPermissionDenied(): void {
    this.permissionDeniedEndpoints.clear();
    console.log(`✅ [API] Cleared all permission denied status`);
  }

  /**
   * Clear all unauthorized status
   */
  public static clearAllUnauthorized(): void {
    this.unauthorizedEndpoints.clear();
    console.log(`✅ [API] Cleared all unauthorized status`);
  }

  /**
   * Clear all tracked error status (both permission and auth)
   */
  public static clearAllErrorStatus(): void {
    this.permissionDeniedEndpoints.clear();
    this.unauthorizedEndpoints.clear();
    console.log(`✅ [API] Cleared all error status (permission and auth)`);
  }

  // Identify auth endpoints that shouldn't be cached
  private static isAuthEndpoint(endpoint: string): boolean {
    return endpoint === '/users/login' || endpoint === '/users/register';
  }

  // Identify public endpoints that should NOT force redirect on 401
  private static isPublicEndpoint(endpoint: string): boolean {
    // Allow browsing exercises without auth and health endpoint
    return endpoint === '/exercises' ||
           endpoint.startsWith('/exercises/') ||
           endpoint === '/health' ||
           endpoint === '/api/health';
  }

  // Endpoints whose 403 carries route-specific context (TODO.md H1's workout edit-window rejection is
  // the first case) rather than a permission/ownership denial. The generic "Access denied ... contact
  // an administrator" copy below is correct for genuine permission failures (admin routes, template/
  // exercise ownership) but would be actively misleading here, so these endpoints surface the server's
  // own `message` instead and skip "permission denied" endpoint tracking - an expired edit window isn't
  // a state that stops applying, so there's nothing gained by caching it as permanently denied.
  private static isRouteScopedForbiddenEndpoint(endpoint: string): boolean {
    return /^\/workout-history\/[^/?]+$/.test(endpoint);
  }

  /**
   * Makes a fetch request to the backend API
   * @param endpoint - API endpoint (e.g., '/exercises')
   * @param options - Fetch options
   * @returns Parsed JSON response
   */
  protected async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    try {
      await BaseApiService.throttleIfPrerendering();

      // Check if this endpoint has been marked as permission denied
      if (BaseApiService.isPermissionDenied(endpoint)) {
        console.log(`🚫 [API] Skipping request to permission denied endpoint: ${endpoint}`);
        throw new Error(`Access denied: You don't have permission to access ${endpoint}`);
      }

      // Check if this endpoint has been marked as unauthorized
      if (BaseApiService.isUnauthorized(endpoint)) {
        console.log(`🔐 [API] Skipping request to unauthorized endpoint: ${endpoint}`);
        if (!BaseApiService.isPublicEndpoint(endpoint)) {
          BaseApiService.redirectToLogin();
        }
        throw new Error(`Authentication required: Please log in to access ${endpoint}`);
      }

      const token = BaseApiService.getToken();
      
      const headers = new Headers({
        'Content-Type': 'application/json',
        'Accept-Language': BaseApiService.getLocale(),
        ...options.headers,
      });

      // Add Authorization header if token exists
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
        // console.log(`🔐 [API] Adding JWT token to request: ${endpoint}`);
      } else {
        // console.log(`⚠️ [API] No JWT token found for request: ${endpoint}`);
      }

      const response = await fetch(`${BaseApiService.BASE_URL}${endpoint}`, {
        headers,
        ...options,
      });

      if (!response.ok) {
        // Try to parse error body if present, but don't fail if it's empty
        const errorData = await response.json().catch(() => ({}));
        
        // Handle unauthorized (401) specifically
        // Handle permission denied (403) specifically
        if (response.status === 401 && !BaseApiService.isAuthEndpoint(endpoint)) {
          if (!BaseApiService.isPublicEndpoint(endpoint)) {
            BaseApiService.markUnauthorized(endpoint);
            BaseApiService.redirectToLogin();
          }
          throw new Error(`Authentication required: Please log in to access ${endpoint}. Your session may have expired.`);
        }

        if (response.status === 403 && !BaseApiService.isAuthEndpoint(endpoint)) {
          if (BaseApiService.isRouteScopedForbiddenEndpoint(endpoint)) {
            throw new Error(errorData.message || `Access denied: You don't have permission to access ${endpoint}.`);
          }
          BaseApiService.markPermissionDenied(endpoint);
          throw new Error(`Access denied: You don't have permission to access ${endpoint}. Please contact an administrator if you believe this is an error.`);
        }
        
        throw new Error(errorData.message || `API request failed: ${response.status} ${response.statusText}`);
      }

      // No Content responses (204) or empty bodies should just return undefined
      if (response.status === 204) {
        return undefined as unknown as T;
      }

      const text = await response.text();
      if (!text) {
        // Empty body but not 204: treat as undefined
        return undefined as unknown as T;
      }

      return JSON.parse(text) as T;
    } catch (error) {
      console.error(`API request failed [${endpoint}]:`, error);
      throw error;
    }
  }

  /**
   * GET request helper
   */
  protected async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  /**
   * POST request helper
   */
  protected async post<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * PUT request helper
   */
  protected async put<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * DELETE request helper
   */
  protected async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}
